import React, { ComponentPropsWithoutRef, FC, useRef, useState } from 'react';
import cn from 'classnames';
import { ReactComponent as CaretUp } from '../../icons/caret_up.svg';
import { ReactComponent as CaretDown } from '../../icons/caret_down.svg';
import { popupLayoutCssPostfix } from '../../types';
import { usePopupContext } from '../../contexts/Settings';

interface Props extends ComponentPropsWithoutRef<'input'> {
  quantity: number;
  handleOfferQuantityChange: React.Dispatch<React.SetStateAction<number>>;
  containerClasses?: string;
  inputClasses?: string;
}

const BACKEND_MAX_VALUE = 32767;

const OfferQuantity: FC<Props> = ({
  quantity,
  handleOfferQuantityChange,
  containerClasses,
  inputClasses,
  ...rest
}) => {
  const qsInputRef = useRef<HTMLInputElement | null>(null);
  const [isInvalidInput, setIsInvalidInput] = useState<Boolean>(false);
  const { settings } = usePopupContext();

  const handleUpChange = () => {
    handleOfferQuantityChange((value: number) =>
      value > 0 ? Math.min(value + 1, BACKEND_MAX_VALUE) : 1,
    );
  };

  const handleDownChange = () => {
    handleOfferQuantityChange((value) =>
      value > 1 ? Math.min(value - 1, BACKEND_MAX_VALUE) : 1,
    );
  };

  const handleBlurQuantityInput = ({
    target,
  }: React.FocusEvent<HTMLInputElement>) => {
    let { valueAsNumber } = target;
    if (isNaN(valueAsNumber)) {
      valueAsNumber = 1;
    }
    const futureValue = Math.max(valueAsNumber, 1);
    handleOfferQuantityChange(Math.min(futureValue, BACKEND_MAX_VALUE));
    setIsInvalidInput(false);
  };

  const handleOnChangeQuantityInput = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    let { valueAsNumber } = target;
    if (isNaN(valueAsNumber)) {
      setIsInvalidInput(true);
    } else {
      const futureValue = Math.max(valueAsNumber, 1);
      handleOfferQuantityChange(Math.min(futureValue, BACKEND_MAX_VALUE));
      setIsInvalidInput(false);
    }
  };

  return (
    <div className={cn(containerClasses, 'relative')}>
      <input
        {...rest}
        ref={qsInputRef}
        min={1}
        value={isInvalidInput ? '' : quantity}
        type="number"
        inputMode="numeric"
        onBlur={handleBlurQuantityInput}
        onChange={handleOnChangeQuantityInput}
        className={cn(
          inputClasses,
          'text-md w-full text-grey-1 border py-8 px-10 appearance-none focus:shadow-none focus:outline-none h-full pointer:text-md-mobile',
          `Quantity-Selector${popupLayoutCssPostfix(settings)}`,
          rest.disabled && 'text-grey-3 bg-white',
        )}
      />
      <div className="absolute right-0 top-0 h-full w-20 bg-grey-5">
        <ArrowButton
          disabled={quantity >= BACKEND_MAX_VALUE || !!rest.disabled}
          onClick={handleUpChange}
          data-cy="caret-up"
        >
          <CaretUp />
        </ArrowButton>
        <ArrowButton
          disabled={rest.disabled || quantity <= 1}
          onClick={handleDownChange}
          className={'border-t-0'}
          data-cy="caret-down"
        >
          <CaretDown />
        </ArrowButton>
      </div>
    </div>
  );
};

const ArrowButton: FC<
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >
> = ({ className = '', children, ...rest }) => (
  <button
    className={cn(
      className,
      'border h-half w-full flex justify-center items-center focus:shadow-none focus:outline-none',
      rest.disabled
        ? 'cursor-default text-grey-3'
        : 'cursor-pointer hover:text-grey-2 transition-all duration-100',
    )}
    {...rest}
  >
    {children}
  </button>
);

export { BACKEND_MAX_VALUE };
export default OfferQuantity;
